import { IAuditLogEntry } from '@dadohr/shared/domainTypes/auditLog';
import { Doc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_ARR } from '@dadohr/shared/domainTypes/emptyConstants';
import { IExperienceTemplate } from '@dadohr/shared/domainTypes/experience';
import styled from '@emotion/styled';
import { Button, Card, Grid, Typography } from '@material-ui/core';
import { cloneDeep, set } from 'lodash';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader } from '../../../../../components/Loader';
import { SomethingWentWrong } from '../../../../../components/SomethingWentWrong';
import { SwitchWithLabel } from '../../../../../components/SwitchWithLabel';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { CanvasBar } from '../../../../../layout/Canvas';
import { Centered } from '../../../../../layout/Centered';
import { FlexContainer } from '../../../../../layout/Flex';
import { Page } from '../../../../../layout/Page';
import { Section } from '../../../../../layout/Section';
import { useExperienceTemplateById } from '../../../../../services/experiences/template';
import { useCombineLoadingValues } from '../../../../../services/firebase';
import { formatDatePrecise } from '../../../../../services/time';
import { toFullName } from '../../../../../services/user';
import { Json } from '../../../../components/Json';
import { TopNav } from '../../../../layout/TopNav';
import { useAdminUserById } from '../../../../services/adminUsers';
import { useAuditLogForDoc } from '../../../../services/auditLog';
import { useOrgById } from '../../../../services/orgs';
import { OrgNavHeader } from '../../components/OrgNavHeader';

const HEIGHT = 300;

const ChangelogEntryContainer = styled('div')((p) => ({}));

const LazyAdminUserById = ({ adminUserId }: { adminUserId: string }) => {
  const [user] = useAdminUserById(adminUserId);
  if (!user) {
    return <span>{adminUserId}</span>;
  }
  return <span>{toFullName(user.data)}</span>;
};

const traverseChanges = (
  obj: any,
  visitor: (path: (string | number)[], value: any) => void,
  path: (string | number)[] = []
) => {
  if (Array.isArray(obj)) {
    obj.forEach((v, i) => {
      const nextPath = [...path, i];
      traverseChanges(v, visitor, nextPath);
    });
    return;
  }

  if (typeof obj === 'object' && obj !== null && obj._seconds) {
    visitor(path, obj);
    return;
  }

  if (typeof obj === 'object' && obj !== null) {
    Object.entries(obj).forEach(([k, v]) => {
      const nextPath = [...path, k];
      traverseChanges(v, visitor, nextPath);
    });
    return;
  }
};

const withTimestampsAsDates = (d: any) => {
  const toChange: [
    (string | number)[],
    { _seconds: number[]; _nanoseconds?: number[] }
  ][] = [];
  traverseChanges(d, (path, value) => {
    console.log(path, value);
    toChange.push([path, value]);
  });
  return toChange.reduce<any>(
    (m, [path, value]) =>
      set(
        m,
        path,
        value._seconds.map((x) => new Date(x * 1000))
      ),
    d
  );
};

const ChangelogEntryCreate = ({ d }: { d: Doc<IAuditLogEntry> }) => {
  return <Centered height={100}>Create</Centered>;
};

const ChangelogEntryDelete = ({ d }: { d: Doc<IAuditLogEntry> }) => {
  return <Centered height={100}>Delete</Centered>;
};

const ChangelogEntryUpdate = ({ d }: { d: Doc<IAuditLogEntry> }) => {
  const changedKeys = Object.keys(d.data.change).filter(
    (x) => x !== 'updatedAt' && x !== 'updatedBy'
  );

  const updatedBy = d.data.dataSnapshot.updatedBy || '';
  const formattedChanges = useMemo(
    () => withTimestampsAsDates(cloneDeep(d.data.change)),
    [d]
  );

  return (
    <ChangelogEntryContainer>
      <FlexContainer spacing={2}>
        <div>
          {d.data.type[0]} {formatDatePrecise(d.data.at)}{' '}
        </div>
        <div>
          Updated by:{' '}
          {updatedBy ? (
            <LazyAdminUserById adminUserId={updatedBy} />
          ) : (
            'unknown'
          )}
        </div>
      </FlexContainer>

      <FlexContainer marginBottom={2}>
        <Typography variant="caption">{changedKeys.join(', ')}</Typography>
      </FlexContainer>
      <Json data={formattedChanges} />
    </ChangelogEntryContainer>
  );
};

const ChangelogEntry = ({ d }: { d: Doc<IAuditLogEntry> }) => {
  if (d.data.type === 'CREATE') {
    return <ChangelogEntryCreate d={d} />;
  }
  if (d.data.type === 'DELETE') {
    return <ChangelogEntryDelete d={d} />;
  }
  if (d.data.type === 'UPDATE') {
    return <ChangelogEntryUpdate d={d} />;
  }

  return <Centered>Unknown type: {d.data.type}</Centered>;
};

const ChangelogContainer = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'max-content 1fr',
  gridColumnGap: p.theme.spacing(2)
}));

const ChangelogMenuItem = styled('div')<{ selected: boolean }>((p) => ({
  borderBottom: p.theme.custom.border.standard,
  padding: p.theme.spacing(),
  paddingRight: p.theme.spacing(2),
  paddingLeft: 0,
  background: p.selected ? p.theme.custom.colors.blue.w300 : 'none'
}));

const Left = styled('div')((p) => ({
  borderRight: p.theme.custom.border.standard,
  maxHeight: 700,
  overflow: 'auto'
}));

const Right = styled('div')((p) => ({
  maxHeight: 700,
  overflow: 'auto'
}));

const ChangeLog = ({
  ds,
  selected,
  setSelected
}: {
  ds: Doc<IAuditLogEntry>[];
  selected: Doc<IAuditLogEntry> | null;
  setSelected: (nextSelected: Doc<IAuditLogEntry>) => void;
}) => {
  if (selected) {
    console.log('SELECTED', selected);
  }
  return (
    <Card>
      <ChangelogContainer>
        <Left>
          {ds.map((d) => (
            <ChangelogMenuItem key={d.id} selected={d.id === selected?.id}>
              <Button onClick={() => setSelected(d)}>
                {d.data.type[0]} {formatDatePrecise(d.data.at)}{' '}
              </Button>
            </ChangelogMenuItem>
          ))}
        </Left>
        <Right>
          {!selected && (
            <Centered height={200}>
              <Typography variant="caption">Select an item</Typography>
            </Centered>
          )}
          {selected && <ChangelogEntry d={selected} />}
        </Right>
      </ChangelogContainer>
    </Card>
  );
};

export const AuditLog = ({ tpl }: { tpl: Doc<IExperienceTemplate> }) => {
  const [ds, loading, error] = useAuditLogForDoc(tpl, 100);
  useErrorLogger('audit log', error);
  const [hideInconsequentialChanges, setHideInconsequentialChanges] = useState(
    true
  );
  const [selected, setSelected] = useState<Doc<IAuditLogEntry> | null>(null);
  const filteredDs = useMemo(() => {
    if (!ds || !hideInconsequentialChanges) {
      return ds || EMPTY_ARR;
    }
    return ds.filter((d) => {
      if (d.data.type !== 'UPDATE') {
        return true;
      }
      const keys = Object.keys(d.data.change);
      return !keys.every((x) => x === 'updatedAt' || x === 'updatedBy');
    });
  }, [ds, hideInconsequentialChanges]);

  return (
    <Section>
      <CanvasBar>
        <div>
          <SwitchWithLabel
            checked={!hideInconsequentialChanges}
            onChange={() => setHideInconsequentialChanges((x) => !x)}
            label="Show inconsequential changes"
          />
        </div>
        <FlexContainer justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              if (!selected) {
                return setSelected(filteredDs[0] || null);
              } else {
                const i = filteredDs.indexOf(selected);
                console.log(i);
                if (i !== -1 && i !== filteredDs.length - 1) {
                  setSelected(filteredDs[i + 1]);
                }
              }
            }}
          >
            Prev ↓
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              if (!selected) {
                return setSelected(filteredDs[filteredDs.length - 1] || null);
              } else {
                const i = filteredDs.indexOf(selected);
                console.log(i);
                if (i !== -1 && i !== 0) {
                  setSelected(filteredDs[i - 1]);
                }
              }
            }}
          >
            Next ↑
          </Button>
        </FlexContainer>
      </CanvasBar>

      {loading && <Loader height={HEIGHT} />}
      {ds && (
        <ChangeLog
          ds={filteredDs}
          selected={selected}
          setSelected={setSelected}
        />
      )}
    </Section>
  );
};

export const PageOrgExperienceTemplateDetailsAuditLog = ({
  orgId,
  tplId
}: {
  orgId: string;
  tplId: string;
}) => {
  const [values, loading] = useCombineLoadingValues(
    useOrgById(orgId),
    useExperienceTemplateById(tplId)
  );

  if (loading || !values) {
    return <Loader height={500} />;
  }
  const [org, tpl] = values;
  if (!org || !tpl) {
    return <SomethingWentWrong height={500} />;
  }

  console.log('NEW PAGE');

  return (
    <>
      <TopNav>
        <Grid container alignItems="center" justify="space-between">
          <Grid item>
            <OrgNavHeader
              parts={[
                <Link to={`/orgs/${org.id}`}>{org.data.name}</Link>,
                'Exp Tpl',
                <Link to={`/orgs/${org.id}/experience-templates/${tpl.id}`}>
                  {tpl.data.adminTitle || tpl.data.title}
                </Link>,
                'Audit Log'
              ]}
            />
          </Grid>
        </Grid>
      </TopNav>
      <Page style={{ marginTop: 50 }}>
        <AuditLog tpl={tpl} />
      </Page>
    </>
  );
};
